// eyeq — manager cabinet contracts (internal.weern.com island). First
// Firebase-auth manager surface on the eyeq worker (auth class 'manager',
// role from eyeq_members) — the machine twin endpoints keep X-Admin-Secret.
// QA model: a frame in 'in_work' is reviewed against a URL candidate
// (AI-suggested / participant-typed / manager-typed); accept freezes the
// chosen URL into the delivery meta and moves the asset to 'accepted',
// reject_frame ends the asset, reject_url only kills the candidate and
// keeps the frame in the queue.
import { z } from 'zod';
import { EyeqAssetStatusSchema } from './assets';
import { EyeqShipIssueSchema, EyeqShipResSchema } from './quota';

// ── GET /api/eyeq/manager/overview ──────────────────────────────────────────
export const EyeqManagerOverviewLocaleSchema = z.object({
  locale: z.string(),
  target: z.number().int(),
  uploaded: z.number().int(),
  in_work: z.number().int(),
  accepted: z.number().int(),
  rejected: z.number().int(),
  shipped: z.number().int(),
  flagged: z.number().int(),
  ready: z.number().int(),        // ship estimate over accepted (delivery regime)
  url_missing: z.number().int(),  // in_work/accepted with empty url on a url_required cell
});
export const EyeqManagerOverviewResSchema = z.object({
  locales: z.array(EyeqManagerOverviewLocaleSchema),
});
export type EyeqManagerOverviewRes = z.infer<typeof EyeqManagerOverviewResSchema>;

// ── GET /api/eyeq/manager/queue?locale=&status=&limit=&offset= ──────────────
// 'maps' — the protocol-sanctioned Google Maps link for Business and
// Storefronts, derived deterministically from the subject + city + country.
// 'ai_wiki' — reserved for the offline reference-page suggester; the source is
// declared here so imported rows have a home before the suggester itself runs.
export const EyeqUrlCandidateSourceSchema = z.enum(['ai', 'manager', 'participant', 'maps', 'ai_wiki']);
export const EyeqUrlCandidateSchema = z.object({
  id: z.number().int(),
  asset_id: z.string(),
  url: z.string(),
  source: EyeqUrlCandidateSourceSchema,
  note: z.string(),
  status: z.enum(['', 'rejected', 'chosen']),
  created_at: z.string(),
});
export type EyeqUrlCandidate = z.infer<typeof EyeqUrlCandidateSchema>;

export const EyeqManagerQueueQuerySchema = z.object({
  locale: z.string().optional(),
  status: EyeqAssetStatusSchema.default('in_work'),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
  // 1 = only frames with no verdict yet (qa_verdict = ''). Verdicts live in D1,
  // so "unreviewed" is a fact about the row, not about this session.
  unreviewed: z.coerce.number().int().min(0).max(1).default(0),
  // 1 = order the page so near-duplicates of one (participant, cell) sit next
  // to each other and carry a group id — a burst is judged once, not N times.
  group_dups: z.coerce.number().int().min(0).max(1).default(0),
});
export type EyeqManagerQueueQuery = z.infer<typeof EyeqManagerQueueQuerySchema>;

export const EyeqManagerQueueItemSchema = z.object({
  asset_id: z.string(),
  locale: z.string(),
  cat_id: z.string(),
  status: EyeqAssetStatusSchema,
  content_type: z.string(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  original_filename: z.string(),
  captured_at: z.string().nullable(),
  gps_present: z.union([z.literal(0), z.literal(1)]).nullable(),
  created_at: z.string(),
  uploader_public_id: z.string(), // opaque ep_<12hex>, never the Firebase uid
  url_required: z.boolean(),
  dhash: z.string(),              // '' when never computed
  qa_verdict: z.enum(['', 'accepted', 'rejected']),
  reject_reason: z.string(),
  qa_at: z.string(),              // '' until a verdict is recorded
  // Near-duplicate grouping (group_dups=1): frames of one burst share a group
  // index and are returned contiguously. null = judged on its own.
  dup_group: z.number().int().nullable(),
  dup_size: z.number().int(),
  meta: z
    .object({
      subject_name: z.string(),
      disambiguation_details: z.string(),
      category: z.string(),
      sub_category: z.string(),
      indoor_outdoor: z.string(),
      indoor_lighting: z.string(),
      outdoor_lighting: z.string(),
      place_details: z.string(),
      city: z.string(),
      country: z.string(),
      language: z.string(),
      camera_make: z.string(),
      camera_model: z.string(),
      url: z.string(),
      has_text: z.number().int(),
    })
    .nullable(),
  candidates: z.array(EyeqUrlCandidateSchema),
});
export type EyeqManagerQueueItem = z.infer<typeof EyeqManagerQueueItemSchema>;

export const EyeqManagerQueueResSchema = z.object({
  items: z.array(EyeqManagerQueueItemSchema),
  total: z.number().int(), // matching rows before limit/offset
});
export type EyeqManagerQueueRes = z.infer<typeof EyeqManagerQueueResSchema>;

// GET /api/eyeq/manager/asset/:asset_id/image — binary endpoint (original R2
// bytes, content-type from the index; HEIC stays HEIC — the client decodes).
// Query contract only per contracts.md; no res schema.

// ── POST /api/eyeq/manager/qa ───────────────────────────────────────────────
// 'clear' un-does a verdict: the frame returns to the queue with an empty
// qa_verdict. It exists because the review screen is a keyboard instrument —
// a mis-hit must be undoable in one key, and an undo that only lives in the
// browser would be a lie the moment the page reloads.
export const EyeqManagerQaVerdictSchema = z.enum(['accept', 'reject_frame', 'reject_url', 'clear']);

// The closed reject-reason register. These seven survived three review rounds
// over two shipments — every frame the owner rejected fell into one of them,
// which is why they are an enum (one keystroke each) and not free text.
export const EyeqQaRejectReasonSchema = z.enum([
  'pii',            // a person, face, plate, document or screen with personal data
  'blur',           // out of focus / motion blur / unusable quality
  'duplicate',      // the same shot already in the batch
  'crooked',        // skewed or tilted past the point the object reads
  'cut_off',        // the subject leaves the frame
  'wrong_metadata', // the row describes a different object than the photo
  'wrong_url',      // the linked page is not the photographed object
  'other',
]);
export type EyeqQaRejectReason = z.infer<typeof EyeqQaRejectReasonSchema>;

export const EyeqManagerQaReqSchema = z.object({
  asset_id: z.string().min(1),
  verdict: EyeqManagerQaVerdictSchema,
  // accept: the final URL frozen into delivery meta ('' allowed only when the
  // cell is not url_required; 'N/A' allowed per protocol). reject_url: which
  // candidate is being killed.
  url: z.string().trim().max(1000).optional(),
  candidate_id: z.number().int().optional(),
  // '' on every verdict except reject_frame, which requires a real reason
  // (enforced in the handler so the refusal names the field).
  reject_reason: z.union([EyeqQaRejectReasonSchema, z.literal('')]).default(''),
  // accept: the reviewer is looking at the frame, so they answer the delivery
  // field nobody else does — omitted leaves the stored value untouched.
  has_text: z.boolean().optional(),
});
export type EyeqManagerQaReq = z.infer<typeof EyeqManagerQaReqSchema>;

export const EyeqManagerQaResSchema = z.object({
  ok: z.boolean(),
  asset_id: z.string(),
  status: EyeqAssetStatusSchema,
  qa_verdict: z.enum(['', 'accepted', 'rejected']),
  qa_at: z.string().default(''),
});
export type EyeqManagerQaRes = z.infer<typeof EyeqManagerQaResSchema>;

// ── POST /api/eyeq/manager/urlSuggest ───────────────────────────────────────
// Machine suggestions for the URL backfill queue. Deterministic only: a Google
// Maps SEARCH link built from the subject + city + country for Business and
// Storefronts frames, which is the URL form the Capture Protocol itself names
// for that category. No coordinates are used or stored (ADR-0039 §2), and no
// model is called — a reference-page suggester is a separate, secret-bearing
// step that lands its rows under source 'ai_wiki'.
export const EyeqManagerUrlSuggestReqSchema = z.object({
  asset_id: z.string().min(1),
});
export type EyeqManagerUrlSuggestReq = z.infer<typeof EyeqManagerUrlSuggestReqSchema>;

export const EyeqManagerUrlSuggestResSchema = z.object({
  ok: z.boolean(),
  // null when the frame's category has no deterministic suggestion.
  candidate: EyeqUrlCandidateSchema.nullable(),
  note: z.string(), // categorical: '' | not_applicable | already_present
});
export type EyeqManagerUrlSuggestRes = z.infer<typeof EyeqManagerUrlSuggestResSchema>;

// ── POST /api/eyeq/manager/urlCandidate ─────────────────────────────────────
export const EyeqManagerUrlCandidateReqSchema = z.object({
  asset_id: z.string().min(1),
  url: z.string().trim().min(1).max(1000),
  note: z.string().trim().max(300).default(''),
});
export type EyeqManagerUrlCandidateReq = z.infer<typeof EyeqManagerUrlCandidateReqSchema>;

export const EyeqManagerUrlCandidateResSchema = z.object({
  ok: z.boolean(),
  candidate: EyeqUrlCandidateSchema,
});
export type EyeqManagerUrlCandidateRes = z.infer<typeof EyeqManagerUrlCandidateResSchema>;

// ── POST /api/eyeq/manager/ship — manager twin of delivery/ship ─────────────
// actor is ALWAYS the authenticated manager (uid server-side), never a body
// field — that is the difference from the machine EyeqShipReqSchema.
export const EyeqManagerShipReqSchema = z.object({
  statuses: z.array(z.string().min(1)).min(1).default(['accepted']),
  dry_run: z.boolean().default(false),
});
export type EyeqManagerShipReq = z.infer<typeof EyeqManagerShipReqSchema>;
// Res reuses EyeqShipResSchema (rows carry r2_key + shipped names + delivery
// json — everything the client-side ZIP/Excel builder needs).
export { EyeqShipResSchema as EyeqManagerShipResSchema, EyeqShipIssueSchema };
export type EyeqManagerShipRes = z.infer<typeof EyeqShipResSchema>;

// ── GET /api/eyeq/manager/delivery/manifest?locale=&statuses= ───────────────
// Re-export path for an already-shipped batch (statuses=shipped).
export const EyeqManagerManifestResSchema = z.object({
  rows: z.array(
    z.object({
      asset_id: z.string(),
      uuid: z.string(),
      r2_key: z.string(),
      shipped_file: z.string(),
      shipped_json: z.string(),
      original_filename: z.string(),
      // Frozen at ship time; a re-export must reproduce the SAME names, so
      // these come from the stored delivery identity, never recomputed.
      pid: z.string(),
      uuid_seq: z.number().int(),
      delivery_asset_id: z.string(),
    }),
  ),
  skipped: z.array(z.string()),
});
export type EyeqManagerManifestRes = z.infer<typeof EyeqManagerManifestResSchema>;

// ── GET /api/eyeq/manager/delivery/json/:asset_id ───────────────────────────
export const EyeqManagerDeliveryJsonResSchema = z.object({
  asset_id: z.string(),
  shipped_file: z.string(),
  shipped_json: z.string(),
  // '' until the frame ships — the table id only exists once the frame has a
  // Participant ID and a UUID.
  delivery_asset_id: z.string().default(''),
  json: z.record(z.string(), z.unknown()), // 17-key delivery JSON (schema v1.0.3)
});
export type EyeqManagerDeliveryJsonRes = z.infer<typeof EyeqManagerDeliveryJsonResSchema>;

// GET /api/eyeq/manager/delivery/table?locale=&statuses= — text/csv binary
// endpoint (SHIPMENT_HEADERS order); query contract only, no res schema.
